'use client';

import { useCallback, useMemo } from 'react';
import Link from 'next/link';
import {
  Heading,
  Text,
  Flex,
  Grid,
  ArticleCard,
  Pagination,
  CategoryHubSection,
  NavTabs,
  CATEGORY_HUB_TOP_ANCHOR_ID,
  CATEGORY_HUB_SECTION_PREVIEW_SIZE,
} from '@highereducation/hen-ui';
import type {
  TransformedItemPost,
  TransformedCategory,
} from '@highereducation/wordpress';
import { formatDate } from '@hen-site/utils/formatDate';
import { buildPublicPath } from '@hen-site/utils/buildPublicPath';
import { buildCategoryHubPageUrl } from '@hen-site/lib/categories/categoryHubPagination';

/** Shared page size for section previews (matches CategoryHubSection composite). */
const POSTS_PAGE_SIZE = CATEGORY_HUB_SECTION_PREVIEW_SIZE;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CategoryHubViewProps {
  categoryName: string;
  categoryDescription?: string;
  /** WP slug of the current category — used to build the "All" nav link. */
  categorySlug: string;
  /** Tenant identifier (e.g. "psychology.org") used to prefix nav hrefs. */
  tenant: string;
  /** Browser-facing host, resolved server-side, so hrefs drop the tenant segment on the tenant's own host. */
  browserHost?: string;
  /** Leaf hub: full list for PostGrid. Parent hub: pass `[]` — sections use `subcategories[].contentNodes`. */
  posts: TransformedItemPost[];
  subcategories: TransformedCategory[];
  /**
   * Categories to display in the nav bar. When on a subcategory page this
   * will be the sibling categories; on a parent page it will be the children.
   * Falls back to `subcategories` when not provided.
   */
  navCategories?: TransformedCategory[];
  /**
   * Slug used for the "All" tab href. On a subcategory page this is the
   * parent slug; on a parent page it matches `categorySlug`.
   */
  navParentSlug?: string;
  /** Parent category display name (child hub only). Used for ArticleCard brow. */
  parentCategoryName?: string;
  /** Leaf hub URL pagination — omitted on parent hubs (preview + load more only). */
  pagination?: {
    currentPage: number;
    totalPages: number;
  };
}

// ---------------------------------------------------------------------------
// PostGrid
// ---------------------------------------------------------------------------
function PostGrid({
  posts,
  /** Hub root topic for the card brow (e.g. "Careers"), not the subsection heading. */
  cardBrow,
}: {
  posts: TransformedItemPost[];
  cardBrow: string;
}) {
  return (
    <Grid columns={1} columns-md={2} columns-lg={3} gap='md'>
      {posts.map((post) => (
        <ArticleCard
          key={post.id}
          layout='stacked'
          title={post.title}
          href={post.link}
          image={post.image}
          imageAlt={post.imagealt}
          author={post.author}
          date={formatDate(post.date) ?? undefined}
          border='stroke'
          size='compact'
          showImage={false}
          brow={cardBrow}
        />
      ))}
    </Grid>
  );
}

/**
 * Leaf category hub: server-sliced posts with numbered pagination at the bottom.
 */
function LeafCategoryPostGrid({
  posts,
  cardBrow,
  tenant,
  browserHost,
  categorySlug,
  pagination,
}: {
  posts: TransformedItemPost[];
  cardBrow: string;
  tenant: string;
  browserHost?: string;
  categorySlug: string;
  pagination?: CategoryHubViewProps['pagination'];
}) {
  const getPageUrl = useCallback(
    (page: number) =>
      buildCategoryHubPageUrl(tenant, categorySlug, page, browserHost),
    [tenant, browserHost, categorySlug],
  );

  return (
    <Flex direction='column' gap='xl'>
      <PostGrid posts={posts} cardBrow={cardBrow} />
      {pagination && pagination.totalPages > 1 && (
        <Pagination
          mode='link'
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          getPageUrl={getPageUrl}
        />
      )}
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Hub sections (parent = one WP child category per section; leaf = single section)
// ---------------------------------------------------------------------------

interface HubSection {
  key: string;
  title: string;
  /** When false, the page H1 already names the category — skip a duplicate heading. */
  showSectionHeading: boolean;
  posts: TransformedItemPost[];
}

function HubCategorySection({
  section,
  cardBrow,
  sectionHubHref,
}: {
  section: HubSection;
  cardBrow: string;
  sectionHubHref: string;
}) {
  const { posts, title, showSectionHeading } = section;

  if (!showSectionHeading) {
    return null;
  }

  const articles = posts.map((post) => ({
    layout: 'stacked' as const,
    title: post.title,
    href: post.link,
    image: post.image,
    imageAlt: post.imagealt,
    author: post.author,
    date: formatDate(post.date) ?? undefined,
    border: 'stroke' as const,
    size: 'compact' as const,
    showImage: false,
    brow: cardBrow,
  }));

  return (
    <CategoryHubSection
      heading={
        <Heading variant='h3' className='text-neutral-900'>
          {title}
        </Heading>
      }
      sectionId={section.key}
      articles={articles}
      viewAllHref={sectionHubHref}
      previewSize={POSTS_PAGE_SIZE}
      viewAllLinkComponent={Link}
    />
  );
}

// ---------------------------------------------------------------------------
// CategoryHubView
// ---------------------------------------------------------------------------

export default function CategoryHubView({
  categoryName,
  categoryDescription,
  categorySlug,
  tenant,
  browserHost,
  posts,
  subcategories,
  navCategories,
  parentCategoryName,
  pagination,
}: CategoryHubViewProps) {
  const navList = navCategories ?? subcategories;
  const isParentHub = subcategories.length > 0;

  const navTabs = useMemo(() => {
    if (!isParentHub || navList.length === 0) return [];

    const childTabs = navList
      .filter((s) => s.categorySlug)
      .map((s) => ({
        value: s.categorySlug as string,
        label: s.categoryName,
        href: `#${s.categorySlug as string}`,
      }));

    return [
      {
        value: 'all',
        label: 'All',
        href: `#${CATEGORY_HUB_TOP_ANCHOR_ID}`,
      },
      ...childTabs,
    ];
  }, [navList, isParentHub]);

  /** Card eyebrow = hub root ("Careers"), not each subsection title. */
  const articleCardBrow = isParentHub
    ? categoryName
    : parentCategoryName ?? categoryName;

  /** One section per child category — only used on the parent "All" hub. */
  const sections = useMemo<HubSection[]>(() => {
    if (!isParentHub) return [];
    return subcategories
      .filter((s): s is TransformedCategory & { categorySlug: string } =>
        Boolean(s.categorySlug),
      )
      .map((s) => ({
        key: s.categorySlug,
        title: s.categoryName,
        showSectionHeading: true,
        posts: s.contentNodes ?? [],
      }));
  }, [isParentHub, subcategories]);

  return (
    <Flex direction='column' gap='3xl'>
      <Flex
        id={CATEGORY_HUB_TOP_ANCHOR_ID}
        direction='column'
        gap='3xl'
        className='scroll-mt-24'
      >
        <Heading variant='h1' className='text-neutral-900'>
          {categoryName}
        </Heading>
        {categoryDescription && (
          <Text variant='b3' color='subtext'>
            {categoryDescription}
          </Text>
        )}
      </Flex>

      <Flex direction='column' gap='xl'>
        {isParentHub && navTabs.length > 0 && (
          <NavTabs tabs={navTabs} variant='underline' />
        )}
        {isParentHub ? (
          <Flex direction='column' gap='max'>
            {sections.map((section) => (
              <HubCategorySection
                key={section.key}
                section={section}
                cardBrow={articleCardBrow}
                sectionHubHref={buildPublicPath(
                  tenant,
                  `/${section.key}/`,
                  browserHost,
                )}
              />
            ))}
          </Flex>
        ) : posts.length > 0 ? (
          <LeafCategoryPostGrid
            posts={posts}
            cardBrow={articleCardBrow}
            tenant={tenant}
            browserHost={browserHost}
            categorySlug={categorySlug}
            pagination={pagination}
          />
        ) : (
          <Text variant='b3' color='subtext'>
            No articles found in this category.
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
